import type { ValidationIssues } from '~/models/common'
import {
  buildSentinelLiftRecord,
  type SentinelLiftInput,
  type SentinelLiftRecord,
  type SentinelLiftStatus,
} from '~/models/sentinelLift'
import { toIsoTimestamp } from '~/types/dates'
import type { SentinelLiftId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

function invalidRecord(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}

export async function getSentinelLift(
  db: SolaDatabase,
  id: SentinelLiftId,
): Promise<Result<SentinelLiftRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.sentinelLifts, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Every lift including retired ones, in display order. */
export async function listSentinelLifts(
  db: SolaDatabase,
): Promise<Result<readonly SentinelLiftRecord[], StorageError>> {
  try {
    return ok(await db.getAllFromIndex(STORE.sentinelLifts, 'order'))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The lifts the strength step offers this week, in display order.
 *
 * Queried through the `active` index, which only works because the status is
 * stored as a string — IndexedDB has no boolean key type, so a boolean here
 * would leave every record out of the index and this list permanently empty.
 * See the note in `models/sentinelLift.ts`.
 */
export async function listActiveSentinelLifts(
  db: SolaDatabase,
): Promise<Result<readonly SentinelLiftRecord[], StorageError>> {
  try {
    const rows = await db.getAllFromIndex(STORE.sentinelLifts, 'active', 'active')
    return ok([...rows].sort((left, right) => left.order - right.order))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function createSentinelLift(
  db: SolaDatabase,
  input: SentinelLiftInput,
  now: Date,
): Promise<Result<SentinelLiftRecord, StorageError>> {
  const built = buildSentinelLiftRecord(input, toIsoTimestamp(now))
  if (!built.ok) return err(invalidRecord(built.error))

  try {
    await db.put(STORE.sentinelLifts, built.value)
    notifyWritten(STORE.sentinelLifts)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Rename or reorder a lift. Its status is changed through `setSentinelLiftStatus`. */
export async function updateSentinelLift(
  db: SolaDatabase,
  id: SentinelLiftId,
  input: SentinelLiftInput,
  now: Date,
): Promise<Result<SentinelLiftRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.sentinelLifts, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That lift no longer exists.'))
    }

    const built = buildSentinelLiftRecord(input, toIsoTimestamp(now), {
      id: existing.id,
      active: existing.active,
      createdAt: existing.createdAt,
    })
    if (!built.ok) return err(invalidRecord(built.error))

    await db.put(STORE.sentinelLifts, built.value)
    notifyWritten(STORE.sentinelLifts)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Retire a lift, or bring it back.
 *
 * Retiring is not deletion, and that is deliberate: the sets recorded against a
 * lift are real training history, and removing the definition would strand them.
 * A retired lift stops being offered and keeps everything it recorded.
 */
export async function setSentinelLiftStatus(
  db: SolaDatabase,
  id: SentinelLiftId,
  active: SentinelLiftStatus,
  now: Date,
): Promise<Result<SentinelLiftRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.sentinelLifts, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That lift no longer exists.'))
    }

    const record: SentinelLiftRecord = {
      ...existing,
      active,
      updatedAt: toIsoTimestamp(now),
    }

    await db.put(STORE.sentinelLifts, record)
    notifyWritten(STORE.sentinelLifts)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
